import {
  ChipBadge,
  IStyles,
  IconWrapper,
  Loader,
  MobilePopup,
  NoContentCard,
  QuantityButton,
  QuestionContainerWithSolution,
  deserify,
  getTeacherSubjectName,
  pxToRem,
  pxTovW,
  theme,
  useCommonServiceClientContext,
  getDeviceType,
  downloadPdfToAndroid,
  formatErrorMessages,
  uploadFile,
  getMediaBasePath,
  getFileSizeInMB,
  getFileExtensionType,
  getFileType,
  QuestionSourceTags,
  InputField,
} from '@geneo2-web/shared-ui';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CloudUpload,
  QuestionModuleMap,
  TaskInfo,
} from '@protos/learning_management/lms.db_pb';
import { useGlobalContext } from '../../../../../app/Context/GlobalContextProvider';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  bloomLevelValuesArray,
  difficultyValuesArray,
  questionValuesArray,
  toBloomLevelEnum,
  toDifficultyEnum,
  toQuestionEnum,
  toStringQuestion,
} from '../../../../../utils/functions';
import { useTextBookQuestionEnabled } from '../../../../../utils/customHooks';
import {
  useAppDispatch,
  useAppSelector,
  useDeserifiedSelector,
} from '../../../../../reduxStore/reduxHooks';
import { AssessmentTask } from '@protos/learning_management/lms.assessment.common.apis_pb';
import {
  AssessmentCreationEnum,
  PaginationInfo,
} from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { Question } from '@protos/content_management/content.db_pb';
import { EditWarningPopup } from '../../../../../components/EditWarningPopup';
import {
  setToastInfo,
  setToastInfoV2,
} from 'apps/teacher/src/pages/Home/reducer/homeDashboard.slice';
import { useDownloadContext } from '../../../../../app/Context/DownloadContextProviderV2';
import { interactionEvent } from '../../../../Auth/auth.events';
import {
  setQPGSearchQuestionsConfig,
  CustomUploadFileResponse,
  QuestionSourceFilter,
  setQuestionUploadFilter,
} from '../../../reducer/assessmentFlow.slice';
import { ConnectError } from '@bufbuild/connect';
import { useAddOrReplaceQPGQuestionMultiSubject } from '../../../QuestionPaperGenerator/ReviewQuestionPaper/utils';
import AiGeneratedButton from '../../../QuestionPaperGenerator/ReviewQuestionPaper/Buttons/AiGeneratedButton';
import { AddPromptPopup } from '../../../QuestionPaperGenerator/SearchQuestions/components/AddPromptPopup';
import { getDifficultyLevelFromString } from '../../../Common/ReviewAssessment/functions';
import { ValidationPopup } from '../../../QuestionPaperGenerator/QuestionPaperCreate/components/ValidationPopup';
import { QPG_ASSESSMENT_REVIEW_PAPER } from 'apps/teacher/src/routeHandling/RoutesNomenclature';
import { AddQuestionsPopup } from '../../../components/UploadQuestions/AddQuestionPopup';
import { APIResponse_StatusEnum } from '@protos/common/common.db_pb';
import {
  FileUploadPurpose,
  MimeEnum,
  SignedUrlReponse,
} from '@protos/learning_management/lms.common.apis_pb';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { IUploadStatusMessage } from '../../../components/UploadQuestions/UploadErrorCard';
import { Timestamp } from '@bufbuild/protobuf';
import {
  getChildQuestionsForMeta,
  getUniqueQuestionIdsFromQpgContent,
} from '../../../utils/QPGv2.utils';

const styles: IStyles = {
  root: {
    bgcolor: '#F8F8FC',
    boxSizing: 'border-box',
    // overflow: { md: 'hidden', xs: 'auto' },
    px: { xs: pxToRem(20), lg: pxTovW(240) },
  },
  mainContainer: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    gap: { xs: pxTovW(40) },
    overflow: 'scroll',
    height: '100%',
  },
  leftPanel: {
    flexBasis: { md: pxTovW(550), lg: pxTovW(615) },
    display: 'flex',
    flexDirection: 'column',
    gap: { md: pxTovW(30) },
  },
  rightPanel: {
    flexBasis: { md: pxTovW(1000) },
    paddingBottom: { xs: pxToRem(70), md: 0 },
    mb: { xs: pxToRem(20), md: pxTovW(80) },
    overflow: { xs: 'initial', md: 'scroll' },
    flexGrow: 1,
    background: '#fff',
    borderRadius: { xs: pxToRem(10), md: pxTovW(15) },
    boxShadow: '0px 0px 10px #E7E7E74A',
    p: { xs: pxToRem(10), md: pxTovW(20) },
    display: 'flex',
    flexDirection: 'column',
    // alignItems: 'flex-start',
    gap: { xs: pxToRem(10), md: pxTovW(30) },
    maxHeight: { md: '70vh' },
    position: 'relative',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(20), md: pxTovW(20) },
    // padding: {
    //   md: `${pxTovW(30)} ${pxTovW(40)}`,
    // },
    // bgcolor: 'common.white',
  },
  heading: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    justifyContent: 'space-between',
    alignItems: 'center',
    // borderBottom: { md: '1px solid grey' },
    py: { xs: pxToRem(20), md: pxTovW(30) },
  },
  textBox: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: { xs: pxToRem(10), md: pxTovW(0) },
    gap: { xs: pxToRem(10), md: pxTovW(10) },
    '& > * ': { display: 'inline' },
    marginBottom: { xs: pxToRem(10), md: pxTovW(0) },
  },
  questionBank: {
    // bgcolor: 'common.white',
    padding: { md: pxTovW(30) },
    paddingBottom: { xs: pxToRem(70), md: 0 },
    borderRadius: { xs: pxToRem(30), md: pxTovW(30) },
    // border: '1px solid red',
    // overflowY: 'scroll',
  },
  selectContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(8), md: pxTovW(10) },
  },
};

export const QPGQuestionBank = () => {
  const [searchText, setSearchText] = useState<string>('');
  const [appliedSearchText, setAppliedSearchText] = useState<string>('');
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { setSelectedFunction } = useGlobalContext();
  const navigate = useNavigate();
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const { isOffline } = useDownloadContext();
  const dispatch = useAppDispatch();
  const fromPath = location.state?.from;
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const isTextBookQuestionEnabled = useTextBookQuestionEnabled();
  const {
    qpg_settings_config,
    qpg_search_questions_config,
    current_qpg_template_info,
    qpg_content,
    selected_question_index_for_alternate,
    selected_question_type_for_alternate,
    qpg_selected_section_index,
    qpg_selected_sub_section_index,
    question_upload_filter,
  } = deserify(useAppSelector((state) => state.assessment));

  const [searchQuestionModuleMap, setSearchQuestionModuleMap] = useState<
    QuestionModuleMap[]
  >([]);
  const [loading, setLoading] = useState<'loading' | 'success' | 'error'>(
    'success'
  );
  const MAX_FILE_LIMIT_MB = 50;
  const class_subject_info = useDeserifiedSelector(
    (state) => state.homeDashboard.class_subject_info
  );
  const isAlternateQuestion =
    typeof selected_question_index_for_alternate == 'number';

  // V2 only: use qpg_content (aligned with QPGReviewPaperSection)
  const assessmentContent = qpg_content?.assessmentContent ?? [];
  const sectionToShow =
    qpg_content?.multiSubjectSections[qpg_selected_section_index ?? 0];
  const multiSubjectSubSections = sectionToShow?.multiSubjectSubSections ?? [];
  const getFormattedQuestionFromId = (id: string) => {
    const ques = assessmentContent?.find((q) => q.questionId === id);
    return ques ? new Question(ques) : undefined;
  };
  const questionsList = (includeChildQuestions = false): Question[] =>
    multiSubjectSubSections
      .flatMap((subSection) =>
        (subSection.subSectionMetaData ?? []).flatMap((quesList) => [
          getFormattedQuestionFromId(quesList.questionId ?? ''),
          ...(includeChildQuestions
            ? getChildQuestionsForMeta(quesList).map((child) =>
                getFormattedQuestionFromId(child.questionId ?? '')
              )
            : []),
        ])
      )
      .filter((val): val is Question => val !== undefined);
  const qpgAssessmentQuestions = questionsList(false);
  const qids = qpgAssessmentQuestions
    .map((q) => q.questionId ?? '')
    .filter(Boolean);
  const qpgQuestionsSequenceInfo = new TaskInfo(
    qpg_content?.questionsSequenceInfo
  );
  const questionSectionMetaInfo =
    qpgQuestionsSequenceInfo?.questionSectionMetaInfo?.sort(
      (a, b) => (a?.sectionNo ?? 0) - (b?.sectionNo ?? 0)
    ) || [];
  const {
    LmsAssessmentTeacherAPIServiceV8WithStatusCodeHandler,
    LmsAssessmentTeacherAPIServiceV1WithStatusCodeHandler,
    TeacherUploadQuestionV1ClientWithStatusCodeHandler,
    LmsCommonAPIServiceV2ClientWithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const [difficultyModalState, setDifficultyModalState] = useState(false);
  const [quesTypeModalState, setQuesTypeModalState] = useState(false);
  const [bloomLevelModalState, setBloomLevelModalState] = useState(false);
  const [chapterModalState, setChapterModalState] = useState(false);
  const [topicModalState, setTopicModalState] = useState(false);
  const [questionUsageModalState, setQuestionUsageModalState] = useState(false);
  const [validationStatus, setValidationStatus] = useState<
    { type: 'success' | 'error'; heading?: string; message: string } | undefined
  >(undefined);
  const [selectedDifficulty, setSelectedDifficulty] = useState<string | null>(
    null
  );
  const [selectedBloomLevel, setSelectedBloomLevel] = useState<string | null>(
    null
  );
  const [selectedQuestionType, setSelectedQuestionType] = useState<
    string | null
  >(
    (selected_question_type_for_alternate
      ? toStringQuestion(selected_question_type_for_alternate)
      : null) || null
  );
  const [selectedChapterId, setSelectedChapterId] = useState<number | null>(
    null
  );
  const [questionList, setQuestionList] = useState<Question[]>([]);
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [paginationInfo, setPaginationInfo] = useState<
    PaginationInfo | undefined
  >(undefined);
  const [isFetchingQuestions, setIsFetchingQuestions] = useState(false);
  const [isAiQuestionResults, setIsAiQuestionResults] = useState(false);
  const isFetchQuestionsInFlightRef = useRef(false);

  const selectedBloomLevelEnum = useMemo(() => {
    return selectedBloomLevel ? toBloomLevelEnum(selectedBloomLevel) : null;
  }, [selectedBloomLevel]);

  const selectedQuestionTypeEnum = useMemo(() => {
    return selectedQuestionType ? toQuestionEnum(selectedQuestionType) : null;
  }, [selectedQuestionType]);

  const selectedDifficultyEnum = useMemo(() => {
    return selectedDifficulty ? toDifficultyEnum(selectedDifficulty) : null;
  }, [selectedDifficulty]);

  const [selectedTopicId, setSelectedTopicId] = useState<number | null>(null);
  const [selectedQuestionUsage, setSelectedQuestionUsage] = useState<
    string | null
  >('All');

  const [popupState, setPopupState] = useState<boolean>(false);
  const [FileUploading, setFileUploading] = useState(false);
  const [File, setFile] = useState<CustomUploadFileResponse | undefined>();
  const [uploadStatusMessage, setUploadStatusMessage] =
    useState<IUploadStatusMessage>({});
  const [addPromptPopupOpen, setAddPromptPopupOpen] = useState<boolean>(false);
  const [openFileUploadPopup, setOpenFileUploadPopup] = useState(false);
  const [templateDownloading, setTemplateDownloading] = useState(false);
  const [popupDetails, setPopupDetails] = useState<{
    title?: string;
    yesHandler?: () => void;
    yesToast?: string;
  }>({});
  // const [replacedQuestionIds, setReplacedQuestionIds] = useState<string[]>([]);
  const qpgAssessmentTask = new AssessmentTask(qpg_content?.assessment);
  const chapters =
    qpgAssessmentTask?.taskMetaInfo?.chapterInfo?.chapterTopicsInfo.filter(
      (chapter) =>
        chapter.subjectId ===
        qpg_content?.multiSubjectSections[qpg_selected_section_index ?? 0]
          ?.subjectId
    ) ?? [];
  const moduleIds =
    chapters
      ?.flatMap((chapter) => [
        chapter.chapterId,
        ...(chapter.topicInfo?.map((topic) => topic.topicId) ?? []),
      ])
      ?.sort((a, b) => a - b) ?? [];
  const addOrReplaceQuestion = useAddOrReplaceQPGQuestionMultiSubject();
  const chapterOptions = chapters.map(
    ({ chapterNo, name, chapterId, topicInfo }) => ({
      label: `${chapterNo}. ${name}`,
      value: name,
      chapterId,
      topicInfo: topicInfo ?? [],
    })
  );
  const selectedChapter =
    chapterOptions.find((c) => c.chapterId === selectedChapterId) || null;
  const topicOptions = (
    selectedChapter
      ? selectedChapter.topicInfo
      : chapters.flatMap((c) => c.topicInfo ?? [])
  ).map(({ name, topicId }) => ({ label: name, value: name, topicId }));

  // --------------------------------------------------------------
  const updateFile = async (initialFile: CustomUploadFileResponse) => {
    setFile(initialFile);
  };

  const FileUpload = async (filesToUpload: FileList) => {
    const file = filesToUpload[0];
    console.log('file:', file, 'filesToUpload:', filesToUpload);
    if (!file) return;
    if (
      ![
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ].includes(file.type)
    ) {
      dispatch(
        setToastInfoV2({
          label: 'Please upload a valid Excel file (.xls or .xlsx)',
          variant: 'error',
          open: true,
        })
      );

      return;
    }

    const tempUserUploads: CustomUploadFileResponse = {
      uploadFileData: new CloudUpload({
        gcpFileUrl: '',
        personId: user_info?.teacherProfileId,
        fileSizeInMb: getFileSizeInMB(file.size),
        isDeleted: false,
        externalWebResourceUrl: '',
        fileName: file.name,
        fileType: getFileType(file.type),
        fileExtensionType: getFileExtensionType(file.type),
        isUploaded: false,
        signedUrl: '',
      }),
      fileUploadStatus: 'pending',
      fileSize: file.size,
    };

    await updateFile(tempUserUploads);

    //! max file size to be 50 MB
    if (getFileSizeInMB(file.size) > MAX_FILE_LIMIT_MB) {
      tempUserUploads.uploadFileData.isUploaded = false;
      tempUserUploads.fileUploadStatus = 'Size Exceeded';
      dispatch(
        setToastInfoV2({
          label: 'File Size should be less than 50 MB',
          variant: 'error',
          open: true,
        })
      );
      return;
    }
    setFileUploading(true);
    try {
      const signedUrlResponse: SignedUrlReponse =
        await LmsCommonAPIServiceV2ClientWithStatusCodeHandler.getSignedUrlForFileUpload(
          {
            personId: user_info?.teacherProfileId,
            filesInfo: [
              {
                originalFilePath: file.name,
                mimeType: MimeEnum.MIME_XLSX,
                size:
                  getFileSizeInMB(file.size) < 0.01
                    ? 0.01
                    : getFileSizeInMB(file.size),
              },
            ],
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
            fileUploadPurpose: FileUploadPurpose.FILE_UPLOAD_TEACHER_QUESTION,

            schoolId: user_info?.schoolDetails[0]?.schoolId || undefined,
          }
        );

      if (signedUrlResponse && signedUrlResponse.response.length > 0) {
        const signedUrl = getMediaBasePath(
          signedUrlResponse.response[0].signedUrl
        );
        const uploadSuccess = await uploadFile({ file, signedUrl });
        if (uploadSuccess.success === true) {
          tempUserUploads.uploadFileData.signedUrl = signedUrl;
          tempUserUploads.uploadFileData.isUploaded = true;
          tempUserUploads.fileUploadStatus = 'success';

          const response =
            await TeacherUploadQuestionV1ClientWithStatusCodeHandler.upsertTeacherQuestions(
              {
                gcsFilePath: signedUrl,
                schoolId: user_info?.schoolDetails[0]?.schoolId,
                moduleIds: moduleIds,
                teacherId: user_info?.teacherProfileId,
                subjectId:
                  qpg_content?.multiSubjectSections[
                    qpg_selected_section_index ?? 0
                  ]?.subjectId,
                sectionId: class_subject_info?.sectionId,
              }
            );

          if (response.status === APIResponse_StatusEnum.STATUS_SUCCESS) {
            tempUserUploads.uploadFileData.isUploaded = true;
            tempUserUploads.fileUploadStatus = 'success';
            setUploadStatusMessage({
              successMessage: response.message || 'File uploaded successfully',
            });
            fetchQuestions();
          } else {
            console.log(
              'response.validationErrors:',
              response.validationErrors
            );
            tempUserUploads.uploadFileData.isUploaded = false;
            tempUserUploads.fileUploadStatus = 'Upload Error';
            setUploadStatusMessage({ errors: response.validationErrors });
          }
        } else {
          tempUserUploads.uploadFileData.isUploaded = false;
          tempUserUploads.fileUploadStatus = 'Upload Error';
          dispatch(
            setToastInfoV2({
              label: `Error Occurred! Unable to upload the file.`,
              variant: 'error',
              open: true,
            })
          );
        }
        setFileUploading(false);
      } else {
        tempUserUploads.uploadFileData.isUploaded = false;
        tempUserUploads.fileUploadStatus = 'Upload Error';
        setUploadStatusMessage({ errors: ['Failed to get signed URL'] });
      }
    } catch (error: any) {
      console.log('error:', error);
      setFileUploading(false);
      tempUserUploads.uploadFileData.isUploaded = false;
      tempUserUploads.fileUploadStatus = 'Upload Error';
      setUploadStatusMessage({ errors: [formatErrorMessages(error.message)] });
    }
  };
  const downloadTemplate = async () => {
    // setTemplateDownloaded(true);
    setTemplateDownloading(true);
    try {
      dispatch(
        setToastInfoV2({
          label: `Downloading template.`,
          variant: 'success',
          open: true,
        })
      );

      const response =
        await TeacherUploadQuestionV1ClientWithStatusCodeHandler.downloadTeacherQuestionsTemplate(
          {
            subjectId:
              qpg_content?.multiSubjectSections[qpg_selected_section_index ?? 0]
                ?.subjectId,
            teacherId: user_info?.teacherProfileId,
            sectionId: class_subject_info?.sectionId,
            schoolId: user_info?.schoolDetails[0]?.schoolId,
          }
        );

      console.log('response:', response);
      if (response.signedUrl) {
        if (getDeviceType() === 'android') {
          downloadPdfToAndroid(response.signedUrl);
        } else {
          const link = document.createElement('a');
          link.href = response.signedUrl;
          link.setAttribute('download', 'Question_Upload_Template.xlsx'); //or any other extension
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }

        setTemplateDownloading(true);
      } else {
        dispatch(
          setToastInfoV2({
            label: `Error Occurred! Unable to download template.`,
            variant: 'error',
            open: true,
          })
        );
      }

      setTemplateDownloading(false);
    } catch (error) {
      // console.log('error:', error);
      dispatch(
        setToastInfoV2({
          label: `Error Occurred! Unable to download template.`,
          variant: 'error',
          open: true,
        })
      );
      setTemplateDownloading(false);
    }
  };
  const handleDelete = async (questionId: string) => {
    try {
      setLoading('loading');
      await TeacherUploadQuestionV1ClientWithStatusCodeHandler.deleteTeacherQuestions(
        {
          teacherId: user_info?.teacherProfileId,
          schoolId: user_info?.schoolDetails[0]?.schoolId,
          questionIds: [questionId],
        }
      );
      dispatch(
        setToastInfoV2({
          label: 'Question deleted successfully',
          variant: 'info',
          open: true,
        })
      );
      setLoading('success');
      fetchQuestions();
    } catch (error) {
      console.error('Error deleting question:', error);
      dispatch(
        setToastInfoV2({
          label: 'Error deleting question',
          variant: 'error',
          open: true,
        })
      );
    } finally {
      setLoading('success');
    }
  };

  const resetTopicId = useCallback(
    (chapterId: number | null) => {
      if (chapterId) {
        if (selectedTopicId) {
          const topic = topicOptions.find((t) => t.topicId === selectedTopicId);
          if (!topic) {
            setSelectedTopicId(null);
          }
        }
      }
    },
    [selectedTopicId, topicOptions]
  );

  // --------------------------------------------------------------

  const handleTopicSelect = async (value: string | null) => {
    if (!value) {
      setSelectedTopicId(null);
      return;
    }

    let topicId: number | null = null;
    let parentChapterId: number | null = null;

    for (const chapter of chapters) {
      const topic = chapter.topicInfo?.find((t) => t.name === value);
      if (topic) {
        topicId = topic.topicId;
        parentChapterId = chapter.chapterId;
        break;
      }
    }

    setSelectedTopicId(topicId);
    if (parentChapterId != null) {
      setSelectedChapterId(parentChapterId);
    }

    await interactionEvent({
      context: 'Search_by_topic',
      name: value.toUpperCase().replace(/\s+/g, '_').replace(/[:.]/g, ''),
      pathSegments: pathSegments,
      isOffline: isOffline,
    });
  };

  // --------------------------------------------------------------

  const getFilterByPreviouslyUsed = (
    questionUsage: string | null
  ): boolean | undefined => {
    if (!questionUsage || questionUsage === 'All') return undefined;
    if (questionUsage === 'Previously Used') return true;
    if (questionUsage === 'Not Used') return false;
    return undefined;
  };

  // --------------------------------------------------------------

  // Build moduleIds for getModuleQuestionsWithFilters from chapter/topic filters.
  const getModuleIdsForQuestionsFilter = (): number[] => {
    const allModuleIds =
      chapterOptions
        .flatMap((chapter) => [
          chapter.chapterId,
          ...chapter.topicInfo.map((topic) => topic.topicId),
        ])
        .sort((a, b) => a - b) ?? [];

    const hasChapter = selectedChapterId != null;
    const hasTopic = selectedTopicId != null;

    if (hasChapter && hasTopic) {
      return [selectedChapterId, selectedTopicId];
    }
    if (hasChapter) {
      return [selectedChapterId];
    }
    if (hasTopic) {
      return [selectedTopicId];
    }
    return allModuleIds;
  };

  // --------------------------------------------------------------

  const fetchQuestions = async ({
    prompt,
    qIdsToBeIgnoredOverride,
    userFilter,
    searchTextOverride,
  }: {
    prompt?: string;
    qIdsToBeIgnoredOverride?: string[];
    userFilter?: QuestionSourceFilter;
    searchTextOverride?: string;
  } = {}) => {
    if (isFetchQuestionsInFlightRef.current) return;
    isFetchQuestionsInFlightRef.current = true;
    try {
      const moduleIds = getModuleIdsForQuestionsFilter();
      setLoading('loading');
      setIsFetchingQuestions(true);
      const ignoreIds = new Set<string>([
        ...(qIdsToBeIgnoredOverride ?? qids),
        ...getUniqueQuestionIdsFromQpgContent({
          qpgContent: qpg_content,
        }),
      ]);
      const effectiveSearchText = searchTextOverride ?? appliedSearchText;
      const effectiveFilter = userFilter ?? question_upload_filter?.value;
      const isAiQuestionFetch =
        qpg_settings_config?.allow_ai_questions &&
        qpg_search_questions_config?.prompt_validated === 'success';
      const response =
        await LmsAssessmentTeacherAPIServiceV8WithStatusCodeHandler.getModuleQuestionsWithFilters(
          {
            teacherId: user_info?.teacherProfileId,
            // subjectId: qpgAssessmentTask?.subjectId,
            createdByUserId:
              effectiveFilter === 'teacher'
                ? user_info?.teacherProfileId
                : undefined,
            schoolId:
              effectiveFilter === 'teacher'
                ? user_info?.schoolDetails[0]?.schoolId
                : undefined,
            textbookQuestions:
              effectiveFilter === 'text-book-questions' ? true : undefined,
            subjectId:
              qpg_content?.multiSubjectSections[qpg_selected_section_index ?? 0]
                ?.subjectId,
            sectionId:
              qpgAssessmentTask?.sectionId ||
              qpg_content?.assessment?.sectionId,
            moduleIds: moduleIds,
            qIdsToBeIgnored: Array.from(ignoreIds),
            aiQuestionPrompts: [qpg_search_questions_config?.prompt || ''],
            noOfQuestions: qpg_search_questions_config?.prompt_validated
              ? 10
              : undefined,
            isAiAssessment:
              qpg_settings_config?.allow_ai_questions &&
              qpg_search_questions_config?.prompt_validated === 'success',
            requiredQuestionTypes: selectedQuestionTypeEnum
              ? [selectedQuestionTypeEnum]
              : [],
            requiredDifficultyTypes: selectedDifficultyEnum
              ? [selectedDifficultyEnum]
              : [],
            bloomLevels: selectedBloomLevelEnum ? [selectedBloomLevelEnum] : [],
            assessmentCreationType:
              AssessmentCreationEnum.ASSESSMENT_CREATION_QUESTION_PAPER,
            pageNumber: selectedPage,
            searchText: effectiveSearchText || undefined,
            filterByPreviouslyUsed: getFilterByPreviouslyUsed(
              selectedQuestionUsage
            ),
          }
        );

      //   const quesList =
      //   qpg_settings_config?.allow_ai_questions &&
      //   qpg_search_questions_config?.prompt_validated === 'success'
      //     ? [...(response.data?.questions || []), ...(questionList || [])]
      //     : response.data?.questions || [];
      // console.log('Fetched Questions:', quesList);
      // const newSearchQuestionModuleMap =
      //   qpg_settings_config?.allow_ai_questions &&
      //   qpg_search_questions_config?.prompt_validated === 'success'
      //     ? [
      //         ...(response.data?.questionModuleMap || []),
      //         ...searchQuestionModuleMap,
      //       ]
      //     : response.data?.questionModuleMap || [];

      const quesList = response.data?.questions || [];
      const newSearchQuestionModuleMap = response.data?.questionModuleMap || [];
      setSearchQuestionModuleMap(newSearchQuestionModuleMap);
      setQuestionList(quesList);
      setPaginationInfo(response.data?.pagination);
      setIsAiQuestionResults(!!isAiQuestionFetch);
      qpg_settings_config?.allow_ai_questions &&
        isAiQuestionFetch &&
        dispatch(
          setToastInfo({
            label: `${
              response.data?.questions?.length || 0
            } AI generated questions added to the question bank`,
            open: true,
            variant: 'info',
          })
        );
      setLoading('success');
    } catch (error) {
      setLoading('error');
    } finally {
      isFetchQuestionsInFlightRef.current = false;
      setIsFetchingQuestions(false);
    }
  };

  // --------------------------------------------------------------

  // // Helper function to build common API request parameters
  // const buildModuleQuestionsParams = (
  //   moduleIds: number[],
  //   currentQuestion: Question,
  //   prompts?: string[]
  // ) => ({
  //   teacherId: user_info?.teacherProfileId,
  //   subjectId: qpgAssessmentTask.subjectId,
  //   sectionId: qpgAssessmentTask?.sectionId,
  //   moduleIds,
  //   assessmentCreationType:
  //     AssessmentCreationEnum.ASSESSMENT_CREATION_QUESTION_PAPER,
  //   noOfQuestions: 1,
  //   requiredQuestionTypes: selectedQuestionTypeEnum
  //     ? [selectedQuestionTypeEnum]
  //     : [],
  //   requiredDifficultyTypes: selectedDifficultyEnum
  //     ? [selectedDifficultyEnum]
  //     : [],
  //   bloomLevels: selectedBloomLevelEnum ? [selectedBloomLevelEnum] : [],
  //   qIdsToBeIgnored: [
  //     ...questionList.map((ques) => ques.questionId || ''),
  //     ...replacedQuestionIds,
  //   ],
  //   currentQuestionId: currentQuestion.questionId,
  //   allowAiQuestions: qpg_settings_config?.allow_ai_questions,
  //   aiQuestionPrompts: prompts || [],
  //   isAiAssessment: qpg_settings_config?.allow_ai_questions,
  // });

  // // --------------------------------------------------------------

  // const handleReplaceAiQuestion = async (
  //   currentQuestion: Question,
  //   prompts?: string[]
  // ) => {
  //   if (!currentQuestion) return;
  //   try {
  //     setLoading('loading');

  //     // Get all module IDs
  //     const allModuleIds =
  //       qpgAssessmentTask.taskMetaInfo?.chapterInfo?.chapterTopicsInfo
  //         .flatMap((chapter) => [
  //           chapter.chapterId,
  //           ...chapter.topicInfo.map((topic) => topic.topicId),
  //         ])
  //         ?.sort((a, b) => a - b) || [];

  //     // Fetch replacement question
  //     const moduleQuestions =
  //       await LmsAssessmentTeacherAPIServiceV8WithStatusCodeHandler.getModuleQuestionsWithFilters(
  //         buildModuleQuestionsParams(allModuleIds, currentQuestion, prompts)
  //       );

  //     if (moduleQuestions?.data?.questions?.length) {
  //       const newQuestion = moduleQuestions.data.questions[0];

  //       // Track replaced questions
  //       if (
  //         currentQuestion.questionId &&
  //         !replacedQuestionIds.includes(currentQuestion.questionId)
  //       ) {
  //         setReplacedQuestionIds([
  //           ...replacedQuestionIds,
  //           currentQuestion.questionId,
  //         ]);
  //       }

  //       // Update question list by replacing the current question with the new one
  //       setQuestionList((prevList) =>
  //         prevList.map((q) =>
  //           q.questionId === currentQuestion.questionId ? newQuestion : q
  //         )
  //       );

  //       // Update search question module map
  //       const newQuestionModuleMapData =
  //         moduleQuestions.data?.questionModuleMap?.find(
  //           (val) => val.questionId === newQuestion.questionId
  //         );

  //       if (newQuestionModuleMapData) {
  //         setSearchQuestionModuleMap((prevMap) => [
  //           ...prevMap.filter(
  //             (val) => val.questionId !== currentQuestion.questionId
  //           ),
  //           newQuestionModuleMapData,
  //         ]);
  //       }

  //       dispatch(
  //         setToastInfo({
  //           label: 'Question replaced successfully',
  //           variant: 'info',
  //           open: true,
  //         })
  //       );
  //     } else {
  //       dispatch(
  //         setToastInfo({
  //           label: 'No questions found to replace',
  //           variant: 'error',
  //           open: true,
  //         })
  //       );
  //     }
  //   } catch (err) {
  //     let message = 'Failed to replace question';
  //     if (err instanceof ConnectError) {
  //       message = err.rawMessage?.replace(/.*\]\s*/, '').trim();
  //     }
  //     console.error('Error replacing AI question:', err);
  //     dispatch(
  //       setToastInfo({
  //         label: message,
  //         variant: 'error',
  //         open: true,
  //       })
  //     );
  //   } finally {
  //     setLoading('success');
  //   }
  // };

  // --------------------------------------------------------------

  const backButtonClick = async () => {
    dispatch(setQuestionUploadFilter({ value: 'all' }));
    if (fromPath) {
      navigate(fromPath, {
        state: {
          isFromQuestionBank: true,
          redirectionPath: location.state?.redirectionPath,
        },
      });
    } else {
      navigate(-1);
    }
  };

  // --------------------------------------------------------------

  const renderSelectMenu = (
    optionList: string[],
    placeholder: string,
    value: string | null,
    onChange: (e: any, value: string | null) => void,
    onMobileClick: () => void,
    modalState: boolean,
    setModalState: (val: boolean) => void,
    disabled?: boolean
  ) => {
    if (isMobile) {
      return (
        <>
          <MobilePopup
            options={optionList}
            title={placeholder}
            modalState={modalState}
            setModalState={setModalState}
            onChange={onChange}
            clearall
          />
          <Box
            sx={{
              display: 'flex',
              height: {
                md: pxTovW(55),
                xs: pxToRem(38),
              },
              border: '1px solid #38FF92',
              borderRadius: '7px',
              boxSizing: 'border-box',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: { xs: pxToRem(10), md: pxTovW(20) },
              width: '100%',
              opacity: disabled ? 0.5 : 1,
            }}
            onClick={disabled ? undefined : onMobileClick}
          >
            <Typography
              // {...params}
              variant="h4"
              sx={{
                px: pxToRem(5),
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
              }}
            >
              {value || placeholder}
            </Typography>
            <KeyboardArrowDownRoundedIcon
              sx={{
                color: 'text.disabled',
              }}
            />
          </Box>
        </>
      );
    }
    return (
      <Autocomplete
        disablePortal
        id="combo-box-demo"
        popupIcon={
          <KeyboardArrowDownRoundedIcon
            sx={{
              color: 'text.disabled',
              fontSize: { xs: 'small', md: 'large' },
            }}
          />
        }
        options={optionList}
        value={value}
        onChange={onChange}
        disabled={disabled}
        // disableClearable
        sx={{
          border: '1px solid #38FF92',
          borderRadius: '7px',
          width: '100%',
          '& .MuiInputBase-root': {
            backgroundColor: 'initial',
            '&:hover': {
              backgroundColor: 'initial',
            },
            '&:focus': {
              backgroundColor: 'initial',
            },
            '&:hover::before': {
              borderBottom: 'none',
            },
            '&:focus::before': {
              borderBottom: 'none',
            },
            '&::before': {
              borderBottom: 'none',
            },
            '&::after': {
              borderBottom: 'none',
            },
          },
        }}
        renderInput={(params) => (
          <TextField variant="filled" {...params} label={placeholder} />
        )}
      />
    );
  };

  // --------------------------------------------------------------

  const addQuestion = async (
    question: Question,
    sectionIndex?: number,
    subSectionIndex?: number,
    questionIndex?: number,
    altIndex?: number
  ) => {
    console.log({
      question,
      sectionIndex,
      subSectionIndex,
      questionIndex,
      altIndex,
    });
    const filteredModuleMap = searchQuestionModuleMap.filter(
      (val) => val.questionId == question.questionId
    );
    console.log({ filteredModuleMap });
    await addOrReplaceQuestion(
      question,
      sectionIndex || 0,
      subSectionIndex || 0,
      questionIndex,
      altIndex,
      filteredModuleMap
    );
    // Refetch so same question cannot be added twice (updated qIdsToBeIgnored)
    await fetchQuestions({
      qIdsToBeIgnoredOverride: [...qids, question.questionId ?? ''].filter(
        Boolean
      ),
    });
    // if (altIndex) {
    //   navigate(QPG_ASSESSMENT_REVIEW_PAPER);
    // }
  };

  // --------------------------------------------------------------

  const handleAddQuestion = (question: Question) => {
    // if not add alternate question, then check if question type exists in any section

    const subSectionName =
      qpg_content?.multiSubjectSections?.[qpg_selected_section_index ?? 0]
        ?.multiSubjectSubSections?.[qpg_selected_sub_section_index ?? 0]
        ?.subSectionName;
    if (!isAlternateQuestion) {
      const sectionMeta =
        qpgQuestionsSequenceInfo?.questionSectionMetaInfo || [];
      setPopupDetails({
        title: `Question will be add to Subsection - ${subSectionName}`,
        yesToast: `Question added to the section`,
        yesHandler: async () => {
          await addQuestion(
            question,
            qpg_selected_section_index,
            qpg_selected_sub_section_index
          );
        },
      });
      setPopupState(true);
      return;
    }
    setPopupDetails({
      title: `Choosing this will add an alternate question`,
      yesToast: `Question added to the section`,
      yesHandler: async () => {
        await addQuestion(
          question,
          qpg_selected_section_index,
          qpg_selected_sub_section_index,
          selected_question_index_for_alternate
        );
      },
    });
    setPopupState(true);
  };

  // --------------------------------------------------------------

  const handleSearchClick = () => {
    const trimmedSearchText = searchText.trim();
    const shouldRefetchDirectly =
      selectedPage === 1 && appliedSearchText === trimmedSearchText;

    setSelectedPage(1);
    setAppliedSearchText(trimmedSearchText);

    if (shouldRefetchDirectly) {
      fetchQuestions({ searchTextOverride: trimmedSearchText });
    }
  };

  const handleClearSearch = () => {
    const shouldRefetchDirectly =
      selectedPage === 1 && appliedSearchText === '';

    setSearchText('');
    setSelectedPage(1);
    setAppliedSearchText('');

    if (shouldRefetchDirectly) {
      fetchQuestions({ searchTextOverride: '' });
    }
  };

  const handleSearchTextChange = (newValue: string) => {
    setSearchText(newValue);

    if (newValue.trim() === '' && appliedSearchText !== '') {
      const shouldRefetchDirectly = selectedPage === 1;
      setSelectedPage(1);
      setAppliedSearchText('');

      if (shouldRefetchDirectly) {
        fetchQuestions({ searchTextOverride: '' });
      }
    }
  };

  // --------------------------------------------------------------

  useEffect(() => {
    setSelectedFunction(() => backButtonClick);
    return () => {
      setSelectedFunction(null);
    };
  }, []);

  // --------------------------------------------------------------

  useEffect(() => {
    fetchQuestions();
  }, [
    selectedChapterId,
    selectedTopicId,
    qpgAssessmentTask?.subjectId,
    user_info?.teacherProfileId,
    selectedBloomLevel,
    selectedDifficulty,
    selectedQuestionType,
    selectedPage,
    appliedSearchText,
    selectedQuestionUsage,
  ]);

  // --------------------------------------------------------------

  useEffect(() => {
    resetTopicId(selectedChapterId);
  }, [selectedChapterId, resetTopicId]);

  // --------------------------------------------------------------

  if (loading === 'loading') return <Loader />;

  return (
    <Box sx={styles.root}>
      <Box sx={styles.heading}>
        {/* <Typography variant="h1">My Homework</Typography> */}
        <Box sx={styles.textBox}>
          <Typography variant="h2">Question Bank </Typography>
          <Typography variant="h3">
            <ChipBadge
              label={
                isAiQuestionResults
                  ? questionList.length
                  : paginationInfo?.totalItems || 0
              }
              color="primary"
              size="small"
            />
          </Typography>
        </Box>
        <Select
          value={question_upload_filter?.value}
          onChange={(e) => {
            dispatch(
              setQuestionUploadFilter({
                value: e.target.value as QuestionSourceFilter,
              })
            );
            fetchQuestions({
              userFilter: e.target.value as QuestionSourceFilter,
            });
          }}
          displayEmpty
          renderValue={() => {
            if (!question_upload_filter?.value) {
              return <Typography color="text.disabled">Select</Typography>;
            }
            if (question_upload_filter?.value === 'all') {
              return <Typography variant="cardText"> All Questions</Typography>;
            }
            if (question_upload_filter?.value === 'text-book-questions') {
              return (
                <Typography variant="cardText"> Textbook Questions</Typography>
              );
            }
            return (
              <Typography variant="cardText">
                {' '}
                Teacher Uploaded Questions
              </Typography>
            );
          }}
          sx={{
            background: '#F5FAFF',
            width: { xs: '100%', md: pxTovW(332) },
            borderRadius: { xs: pxToRem(10), md: pxTovW(5) },
            height: { xs: 'max-content', md: '70%', lg: pxTovW(65) },
            '.MuiOutlinedInput-notchedOutline': { border: '2px solid #87C5FF' },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              border: '2px solid #87C5FF',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: '2px solid #87C5FF',
            },
            boxShadow: 'none',
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: { xs: pxToRem(100), md: pxTovW(300) },
                minHeight: { xs: 'max-content', md: 'max-content' },
                backgroundColor: '#F8FCFF',
                '&::-webkit-scrollbar': { display: 'none' },
                padding: 0,
                borderRadius: { xs: pxToRem(15), md: pxTovW(15) },
                '& .MuiList-root': {
                  backgroundColor: '#F8FCFF',
                  maxHeight: { xs: pxToRem(150), md: pxTovW(300) },
                  padding: 0,
                },

                minWidth: { xs: '95%', md: pxTovW(230) },
                maxWidth: { xs: '95%', md: pxTovW(200), lg: pxTovW(150) },
              },
            },
          }}
        >
          <MenuItem value="all">All Questions</MenuItem>
          <MenuItem value="teacher">Teacher Uploaded Questions</MenuItem>
          {isTextBookQuestionEnabled && (
            <MenuItem value="text-book-questions">Textbook Questions</MenuItem>
          )}
        </Select>
      </Box>
      <Box sx={styles.mainContainer}>
        <Box sx={styles.leftPanel}>
          <Box sx={styles.header}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                padding: { xs: pxToRem(10), md: pxTovW(20) },
                backgroundColor: 'common.white',
                borderRadius: { xs: pxToRem(10), md: pxTovW(15) },
                boxShadow: `0px 0px ${pxToRem(5)} #E7E7E7D9`,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: pxToRem(10), md: pxTovW(20) },
                  paddingBottom: { xs: pxToRem(10), md: pxTovW(20) },
                  marginBottom: { xs: pxToRem(10), md: pxTovW(20) },
                }}
              >
                <InputField
                  variant="outlined"
                  fullWidth
                  onChange={(e) => handleSearchTextChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearchClick();
                    }
                  }}
                  value={searchText}
                  placeholder="Search concepts or subtopics"
                  muiOutlinedInputRoot={{
                    paddingX: {
                      xs: pxToRem(15),
                      md: pxTovW(10),
                      lg: pxTovW(20),
                    },
                    paddingY: {
                      xs: pxToRem(10),
                      md: pxTovW(25),
                      lg: pxTovW(10),
                    },
                  }}
                  sx={{
                    borderRadius: { xs: pxToRem(20), md: pxTovW(20) },
                    border: '1px solid #CCE6FE',
                    '& > div': {
                      background:
                        searchText === '' || searchText === null
                          ? 'transparent'
                          : 'linear-gradient(to right, transparent 88%,#007cdc 88% 100%)',
                      padding: '0px',
                    },
                  }}
                  InputProps={{
                    startAdornment: (searchText === '' ||
                      searchText === null) && (
                      <InputAdornment position="start">
                        <SearchIcon
                          sx={{
                            fontSize: {
                              xs: pxToRem(18),
                              md: pxToRem(20),
                              lg: pxToRem(25),
                            },
                          }}
                          color="primary"
                        />
                      </InputAdornment>
                    ),
                    endAdornment: searchText ? (
                      <InputAdornment position="end">
                        <Box
                          sx={{
                            display: 'flex',
                            gap: { xs: pxToRem(10), md: pxTovW(20) },
                            boxSizing: 'border-box',
                          }}
                        >
                          <IconButton onClick={handleClearSearch} size="small">
                            <CloseIcon
                              color="primary"
                              sx={{
                                fontSize: {
                                  xs: pxToRem(18),
                                  md: pxToRem(20),
                                  lg: pxToRem(25),
                                },
                              }}
                            />
                          </IconButton>
                          <Box sx={{ backgroundColor: 'primary.main' }}>
                            <IconButton
                              onClick={handleSearchClick}
                              size="small"
                            >
                              <SearchIcon
                                sx={{
                                  color: 'white',
                                  fontSize: {
                                    xs: pxToRem(18),
                                    md: pxToRem(20),
                                    lg: pxToRem(25),
                                  },
                                }}
                              />
                            </IconButton>
                          </Box>
                        </Box>
                      </InputAdornment>
                    ) : null,
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    gap: { xs: pxToRem(10), md: pxTovW(20) },
                  }}
                >
                  {renderSelectMenu(
                    difficultyValuesArray,
                    'Difficulty Level',
                    selectedDifficulty ?? null,
                    async (e, value) => {
                      setSelectedDifficulty(value);
                      setSelectedPage(1);
                      value &&
                        (await interactionEvent({
                          context: 'Difficulty_level',
                          name: value.toUpperCase().replace(/\s+/g, '_'),
                          pathSegments: pathSegments,
                          isOffline: isOffline,
                        }));
                    },
                    () => setDifficultyModalState(true),
                    difficultyModalState,
                    setDifficultyModalState
                  )}
                  {renderSelectMenu(
                    // questionValuesArray.filter(
                    //   (questionValue) => questionValue !== 'Competency Based' //todo: add support for this question type
                    // ),
                    questionValuesArray,
                    'Question Type',
                    selectedQuestionType ?? null,
                    async (e, value) => {
                      setSelectedQuestionType(value);
                      setSelectedPage(1);
                      value &&
                        (await interactionEvent({
                          context: 'Question_type',
                          name: value
                            .toUpperCase()
                            .replace(/\s+/g, '_')
                            .replace(/\//g, '_/_'),
                          pathSegments: pathSegments,
                          isOffline: isOffline,
                        }));
                    },
                    () => setQuesTypeModalState(true),
                    quesTypeModalState,
                    setQuesTypeModalState,
                    !!selected_question_type_for_alternate
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: { xs: pxToRem(10), md: pxTovW(20) },
                  }}
                >
                  {renderSelectMenu(
                    bloomLevelValuesArray,
                    'Bloom Level',
                    selectedBloomLevel ?? null,
                    async (e, value) => {
                      setSelectedBloomLevel(value);
                      setSelectedPage(1);
                      value &&
                        (await interactionEvent({
                          context: 'Bloom_level',
                          name: value.toUpperCase().replace(/\s+/g, '_'),
                          pathSegments: pathSegments,
                          isOffline: isOffline,
                        }));
                    },
                    () => setBloomLevelModalState(true),
                    bloomLevelModalState,
                    setBloomLevelModalState
                  )}
                  {/* empty box so that above box takes 50% width */}
                  <Box sx={{ width: '100%' }} />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: pxToRem(10), md: pxTovW(20) },
                    borderTop: '1px dashed #E3E3E3',
                    pt: { xs: pxToRem(10), md: pxTovW(20) },
                  }}
                >
                  <Box sx={styles.selectContainer}>
                    <Typography variant="h4" fontWeight={'bold'}>
                      Search by Chapter
                    </Typography>
                    {renderSelectMenu(
                      chapterOptions.map((e) => e.label),
                      'Chapter',
                      chapterOptions.find(
                        (e) => e.chapterId === selectedChapterId
                      )?.label ?? null,
                      async (e, value) => {
                        const chapterId =
                          chapterOptions.find((e) => e.label === value)
                            ?.chapterId || null;
                        setSelectedChapterId(chapterId);
                        if (!chapterId) {
                          setSelectedTopicId(null);
                        }
                        value &&
                          (await interactionEvent({
                            context: 'Search_by_chapter',
                            name: value
                              .toUpperCase()
                              .replace(/\s+/g, '_')
                              .replace(/[:.]/g, ''),
                            pathSegments: pathSegments,
                            isOffline: isOffline,
                          }));
                      },
                      () => setChapterModalState(true),
                      chapterModalState,
                      setChapterModalState
                    )}
                  </Box>
                  <Box sx={styles.selectContainer}>
                    <Typography variant="h4" fontWeight={'bold'}>
                      Search by Topic
                    </Typography>
                    {renderSelectMenu(
                      topicOptions.map((e) => e.label),
                      'Topic',
                      topicOptions.find((e) => e.topicId === selectedTopicId)
                        ?.label ?? null,
                      async (e, value) => {
                        await handleTopicSelect(value);
                      },
                      () => setTopicModalState(true),
                      topicModalState,
                      setTopicModalState
                    )}
                  </Box>
                  <Box sx={styles.selectContainer}>
                    <Typography variant="h4" fontWeight={'bold'}>
                      Question usage
                    </Typography>
                    {renderSelectMenu(
                      ['Previously Used', 'Not Used', 'All'],
                      'Question usage',
                      selectedQuestionUsage ?? null,
                      async (e, value) => {
                        setSelectedQuestionUsage(value ?? 'All');
                        setSelectedPage(1);
                        value &&
                          (await interactionEvent({
                            context: 'Question_usage',
                            name: value.toUpperCase().replace(/\s+/g, '_'),
                            pathSegments: pathSegments,
                            isOffline: isOffline,
                          }));
                      },
                      () => setQuestionUsageModalState(true),
                      questionUsageModalState,
                      setQuestionUsageModalState
                    )}
                  </Box>

                  {(!!qpg_settings_config?.allow_ai_questions ||
                    !!qpgAssessmentTask?.isAiAssessment) && (
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          p: {
                            xs: `${pxToRem(8)} ${pxToRem(10)}`,
                            md: `${pxTovW(10)} ${pxTovW(12)}`,
                          },
                          borderRadius: { xs: pxToRem(5), md: pxTovW(10) },
                          border: '1px solid #0AA34F',
                          cursor: 'pointer',
                          background: '#CFFFE5',
                          display: 'flex',
                          alignItems: 'center',
                          gap: { xs: pxToRem(5), md: pxTovW(10) },
                        }}
                        onClick={() => {
                          setAddPromptPopupOpen(true);
                        }}
                      >
                        <IconWrapper
                          name="plus_qpg_green"
                          customSx={{ height: '23px', width: '23px' }}
                          type="png"
                          parentFolder="icons"
                        />
                        <Typography
                          variant="cardText"
                          color={'#0AA34F'}
                          fontWeight={'600'}
                        >
                          Add Questions with AI
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        p: {
                          xs: `${pxToRem(8)} ${pxToRem(10)}`,
                          md: `${pxTovW(10)} ${pxTovW(12)}`,
                        },
                        borderRadius: { xs: pxToRem(5), md: pxTovW(10) },
                        border: '1px solid #0AA34F',
                        cursor: 'pointer',
                        background: '#CFFFE5',
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: pxToRem(5), md: pxTovW(10) },
                      }}
                      onClick={() => {
                        setOpenFileUploadPopup(true);
                      }}
                    >
                      <IconWrapper
                        name="blue-plus"
                        customSx={{ height: '23px', width: '23px' }}
                        type="png"
                        parentFolder="icons"
                      />
                      <Typography
                        variant="cardText"
                        color={'#007CDC'}
                        fontWeight={'600'}
                      >
                        Add Your Own Questions
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={styles.rightPanel}>
          {questionList.length > 0 ? (
            questionList.map((q, index) => (
              <Box key={'ques_' + index}>
                <QuestionContainerWithSolution
                  questionNumber={
                    (selectedPage - 1) * (paginationInfo?.itemsPerPage ?? 20) +
                    index +
                    1
                  }
                  question={q}
                  hideMarks
                  showQuestionStats
                  showAnswer
                  imageResizeEnabled={true}
                  key={'ques_' + index}
                  headerLeftElement={
                    <>
                      {q.isAiGenerated ? (
                        <AiGeneratedButton
                        // handlePrompt={(prompt) => {
                        //   handleReplaceAiQuestion(q, [prompt]);
                        // }}
                        />
                      ) : null}
                      <QuestionSourceTags
                        question={q}
                        teacherProfileId={user_info?.teacherProfileId}
                        teacherName={user_info?.userName}
                      />
                    </>
                  }
                  isPreviouslyUsed={
                    searchQuestionModuleMap.find(
                      (m) => m.questionId === q.questionId
                    )?.isPreviouslyUsed
                  }
                  headerRightElement={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: pxToRem(5), md: pxTovW(10) },
                      }}
                    >
                      <Button
                        sx={{ marginLeft: 'auto' }}
                        onClick={async () => {
                          handleAddQuestion(q);
                          await interactionEvent({
                            context: 'Question_bank',
                            name: 'ADD',
                            pathSegments: pathSegments,
                            isOffline: isOffline,
                          });
                        }}
                      >
                        <QuantityButton
                          buttonText={isAlternateQuestion ? 'Choose' : 'Add'}
                          quantity={0}
                          maxQuantity={1}
                        />
                      </Button>
                      {q.createdBy === user_info?.teacherProfileId ? (
                        <IconWrapper
                          name={'deletehw'}
                          size="md"
                          type="png"
                          parentFolder="icons"
                          customSx={{
                            width: {
                              xs: pxToRem(17),
                              md: pxTovW(35),
                              lg: pxTovW(24),
                            },
                            margin: { xs: pxToRem(10), md: 0 },
                          }}
                          onClick={() => handleDelete(q.questionId || '')}
                        />
                      ) : (
                        <></>
                      )}
                    </Box>
                  }
                />
              </Box>
            ))
          ) : (
            <NoContentCard
              variant="info"
              icon="cards"
              text="No Questions Available"
            />
          )}

          {(paginationInfo?.totalPages ?? 1) > 1 && (
            <Pagination
              page={selectedPage}
              count={paginationInfo?.totalPages}
              color="primary"
              disabled={isFetchingQuestions}
              onChange={async (
                event: React.ChangeEvent<unknown>,
                page: number
              ) => {
                if (isFetchingQuestions) return;
                setSelectedPage(page);
              }}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'fixed',
                bottom: 0,
                left: { xs: pxToRem(10), md: 'unset' },
                border: '1px solid #007CDC',
                borderTopLeftRadius: { xs: pxToRem(10), md: pxTovW(16) },
                borderTopRightRadius: { xs: pxToRem(10), md: pxTovW(16) },
                boxShadow: '0px 0px 10px #00000029',

                width: { xs: '95%', md: pxTovW(950), lg: pxTovW(840) },
                height: { xs: pxToRem(55), md: pxTovW(85) },
                marginX: 'auto',
                mb: pxTovW(10),
                backgroundColor: '#E9F5FF',
              }}
            />
          )}
        </Box>
      </Box>

      <EditWarningPopup
        open={popupState}
        handleClose={() => {
          setPopupState(false);
        }}
        yesClickHandler={async () => {
          if (popupDetails.yesHandler) {
            await popupDetails.yesHandler();
          }
          if (popupDetails.yesToast) {
            dispatch(
              setToastInfo({
                label: popupDetails.yesToast,
                open: true,
                variant: 'info',
              })
            );
          }
          setPopupState(false);
        }}
        noClickHandler={() => {
          setPopupState(false);
        }}
        iconname={'correct'}
        text1={popupDetails.title || ''}
        text2={'Would you like to proceed?'}
      />

      <AddPromptPopup
        open={addPromptPopupOpen}
        value={qpg_search_questions_config?.prompt}
        onChange={(val) => {
          dispatch(
            setQPGSearchQuestionsConfig({
              ...qpg_search_questions_config,
              prompt: val,
              prompt_validated: undefined,
            })
          );
        }}
        handleClose={() => {
          dispatch(setQPGSearchQuestionsConfig(undefined));
          setAddPromptPopupOpen(false);
        }}
        addQuestionsHandler={() => {
          fetchQuestions();
          dispatch(
            setQPGSearchQuestionsConfig({
              ...qpg_search_questions_config,
              prompt: undefined,
              prompt_validated: undefined,
            })
          );
          setAddPromptPopupOpen(false);
        }}
        validationEnabled={
          !!qpg_settings_config?.allow_ai_questions ||
          !!qpgAssessmentTask?.isAiAssessment
        }
        validated={qpg_search_questions_config?.prompt_validated}
        onValidateClick={async () => {
          if (!qpg_search_questions_config?.prompt) {
            return;
          }
          try {
            setLoading('loading');
            const res =
              await LmsAssessmentTeacherAPIServiceV1WithStatusCodeHandler.checkValidPrompt(
                {
                  subject:
                    getTeacherSubjectName(
                      qpg_content?.assessment?.subjectId,
                      user_info?.teachClassSubjects
                    ) || '',
                  moduleIds,
                  bloomLevels: qpg_settings_config?.bloomLevels || [],
                  questionSectionInfo:
                    current_qpg_template_info?.questionSectionInfo || [],

                  avgDifficultyLevel: getDifficultyLevelFromString(
                    qpg_settings_config?.overallDifficultyLevel || 'Medium'
                  ),
                  customPrompt: qpg_search_questions_config?.prompt,
                  teacherId: user_info?.teacherProfileId,
                  assessmentCreationType:
                    AssessmentCreationEnum.ASSESSMENT_CREATION_QUESTION_PAPER,
                }
              );
            if (res.isValidated) {
              dispatch(
                setQPGSearchQuestionsConfig({
                  ...qpg_search_questions_config,
                  prompt_validated: 'success',
                })
              );
              setValidationStatus({
                type: 'success',
                message: 'Prompt validated! Ready to use.',
              });
              setAddPromptPopupOpen(false);
            } else {
              dispatch(
                setQPGSearchQuestionsConfig({
                  ...qpg_search_questions_config,
                  prompt_validated: 'error',
                })
              );
              setValidationStatus({
                type: 'error',
                heading: 'Invalid Prompt',
                message: (
                  (res.message || '') + ' Please review and try again.'
                ).trim(),
              });
              setAddPromptPopupOpen(false);
            }
          } catch (err) {
            console.log(err);
            setValidationStatus({
              type: 'error',
              heading: 'Some error occurred',
              message:
                'Error while validating prompt. Please try after some time',
            });
            setAddPromptPopupOpen(false);
          } finally {
            setLoading('success');
          }
        }}
      />

      <ValidationPopup
        open={!!validationStatus}
        handleClose={() => {
          setValidationStatus(undefined);
          setAddPromptPopupOpen(true);
        }}
        okHandler={() => {
          setValidationStatus(undefined);
          setAddPromptPopupOpen(true);
        }}
        iconname={
          validationStatus?.type == 'success'
            ? 'check-green'
            : validationStatus?.type == 'error'
            ? 'wrong'
            : undefined
        }
        text1={validationStatus?.heading}
        text2={validationStatus?.message}
      />
      <AddQuestionsPopup
        open={openFileUploadPopup}
        handleClose={() => {
          setFileUploading(false);
          setFile(undefined);
          setUploadStatusMessage({});
          setOpenFileUploadPopup(false);
        }}
        retryUploadFunction={() => {
          setFileUploading(false);
          setFile(undefined);
          setUploadStatusMessage({});
        }}
        downloadClickHandler={downloadTemplate}
        templateDownloading={templateDownloading}
        instructions={[
          'Download the question template from the DownLoad Template button',
          'Fill in your questions following the format specified in the template',
          'Ensure all required fields are completed (question text, options, correct answer, difficulty level)',
          'Save the file in .xlsx or .csv format',
          'Upload the completed file using the upload section below',
          'Review the uploaded questions before adding them to your question bank',
        ]}
        userUploadedFile={File}
        FileUploading={FileUploading}
        handleChangeFunction={(event) => {
          FileUpload(event.target.files as FileList);
          event.target.value = '';
        }}
        uploadStatusMessage={uploadStatusMessage}
        setUploadStatusMessage={setUploadStatusMessage}
      />
    </Box>
  );
};
